// frontend/src/components/video/VideoStream.jsx - Fixed video frame display
import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { 
  Monitor, 
  Play, 
  Pause, 
  RotateCcw,
  Maximize2,
  Volume2,
  VolumeX
} from 'lucide-react'

const VideoStream = ({ videoFrames, connectionStatus, onFullscreen }) => {
  const [isPlaying, setIsPlaying] = useState(true)
  const [isMuted, setIsMuted] = useState(true)
  const [currentFrame, setCurrentFrame] = useState(null)
  const [streamStats, setStreamStats] = useState({ fps: 0, frameCount: 0 })
  const canvasRef = useRef(null)
  const frameCountRef = useRef(0)
  const lastFpsUpdate = useRef(Date.now())
  const lastFrameTime = useRef(Date.now())
  const fpsInterval = useRef(null)

  // **FIXED: Process video frames from props**
  useEffect(() => {
    console.log('🔍 VideoStream processing frames:', {
      totalFrames: videoFrames.length,
      latestFrame: videoFrames.length > 0 ? videoFrames[videoFrames.length - 1].timestamp : 'none'
    })
    
    if (videoFrames.length > 0) {
      const latestFrame = videoFrames[videoFrames.length - 1]
      
      if (latestFrame.frame && latestFrame.frame !== currentFrame) {
        console.log('🎬 Setting new current frame:', {
          frameLength: latestFrame.frame.length,
          timestamp: latestFrame.timestamp
        })
        
        setCurrentFrame(latestFrame.frame)
        
        // Update frame statistics
        frameCountRef.current += 1
        const now = Date.now()
        lastFrameTime.current = now
        
        // Calculate FPS every second
        if (!fpsInterval.current) {
          fpsInterval.current = setInterval(() => {
            const timeDiff = Date.now() - lastFpsUpdate.current
            if (timeDiff >= 1000) {
              const fps = Math.round((frameCountRef.current * 1000) / timeDiff)
              setStreamStats(prev => ({
                fps,
                frameCount: videoFrames.length
              }))
              frameCountRef.current = 0
              lastFpsUpdate.current = Date.now()
            }
          }, 1000)
        }
      }
    }
    
    // Cleanup FPS interval when no frames
    return () => {
      if (videoFrames.length === 0 && fpsInterval.current) {
        clearInterval(fpsInterval.current)
        fpsInterval.current = null
        setStreamStats({ fps: 0, frameCount: 0 })
      }
    }
  }, [videoFrames, currentFrame])

  // **FIXED: Handle video frame display on canvas**
  useEffect(() => {
    if (currentFrame && isPlaying && canvasRef.current) {
      console.log('🖼️ Drawing frame to canvas, frame length:', currentFrame.length)
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      const img = new Image()
      
      img.onload = () => {
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        
        // Draw frame to fit canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        console.log('✅ Frame drawn successfully to canvas')
      }
      
      img.onerror = (error) => {
        console.error('❌ Error loading video frame:', error)
        console.error('🔍 Frame data preview:', currentFrame.substring(0, 100))
      }
      
      // Ensure the frame data is a proper data URL
      if (currentFrame.startsWith('data:image/')) {
        img.src = currentFrame
      } else {
        console.warn('⚠️ Invalid frame data format:', currentFrame.substring(0, 50))
      }
    }
  }, [currentFrame, isPlaying])

  // **FIXED: Check if stream has stopped**
  useEffect(() => {
    const checkStreamStatus = setInterval(() => {
      const now = Date.now()
      if (now - lastFrameTime.current > 3000 && streamStats.fps > 0) {
        setStreamStats(prev => ({ ...prev, fps: 0 }))
        console.log('📡 Stream appears to have stopped (no frames for 3s)')
      }
    }, 1000)

    return () => clearInterval(checkStreamStatus)
  }, [streamStats.fps])

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying)
    console.log('⏯️ Video playback:', !isPlaying ? 'playing' : 'paused')
  }

  const toggleMute = () => {
    setIsMuted(!isMuted)
  }

  const refreshStream = () => {
    // Reset frame counter and request fresh stream
    frameCountRef.current = 0
    setStreamStats({ fps: 0, frameCount: videoFrames.length })
    lastFrameTime.current = Date.now()
    console.log('🔄 Stream refreshed')
  }

  const getStatusColor = () => {
    if (connectionStatus === 'connected' && streamStats.fps > 0) return 'green'
    if (connectionStatus === 'connected' && currentFrame) return 'yellow'
    if (connectionStatus === 'connected') return 'yellow'
    return 'red'
  }

  const getStatusText = () => {
    if (connectionStatus !== 'connected') return 'Disconnected'
    if (streamStats.fps > 0) return `Live • ${streamStats.fps} FPS`
    if (currentFrame) return 'Connected • Displaying video'
    return 'Connected • Starting...'
  }

  console.log('📈 Current VideoStream state:', {
    videoFrameCount: videoFrames.length,
    hasCurrentFrame: !!currentFrame,
    streamStats,
    connectionStatus,
    isPlaying,
    canvasRef: !!canvasRef.current
  })

  return (
    <div className="relative w-full rounded-2xl overflow-hidden bg-white/20 backdrop-blur-2xl shadow-xl" style={{ aspectRatio: '16/9', minHeight: 340 }}>
      {currentFrame ? (
        <canvas
          ref={canvasRef}
          width={854}
          height={480}
          className="w-full h-full object-contain rounded-2xl"
          style={{ display: isPlaying ? 'block' : 'none' }}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-gray-400 text-lg">Waiting for video stream...</span>
        </div>
      )}
      {/* No overlays, no header/footer */}
    </div>
  )
}

export default VideoStream